<template>
    <section>
        <VCard>
            <VCardText class="d-flex flex-wrap gap-4">
                <VSpacer />

                <div class="d-flex align-center flex-wrap gap-4">
                    <!-- 👉 Search  -->
                    <AppTextField v-model="searchQuery" :placeholder="t('admin_notifications.search_by_name')"
                        density="compact" prepend-inner-icon="tabler-search" style="inline-size: 20rem;" clearable />

                    <!-- 👉 Add screen button -->
                    <VBtn v-if="$can('create', 'screen')" color="success" prepend-icon="tabler-plus"
                        @click="openScreenModal()">
                        {{ $t('admin_notifications.add_new') }}
                    </VBtn>
                </div>
            </VCardText>

            <VDivider />

            <!-- SECTION datatable -->
            <VDataTableServer v-model:items-per-page="itemsPerPage" v-model:page="page" :items-per-page-options="[
                { value: 10, title: '10' },
                { value: 20, title: '20' },
                { value: 50, title: '50' },
                { value: -1, title: '$vuetify.dataFooter.itemsPerPageAll' },
            ]" :items="screens" :items-length="totalScreens" :headers="headers" class="text-no-wrap styled-table"
                @update:options="updateOptions">

                <!-- Active Status -->
                <template #item.is_active="{ item }">
                    <VChip :color="item.is_active ? 'success' : 'error'" rounded="sm" size="small" variant="tonal">
                        {{ item.is_active ? t('admin_notifications.active') : t('admin_notifications.inactive') }}
                    </VChip>
                </template>

                <!-- Sort Order -->
                <template #item.sort_order="{ item }">
                    <span class="text-caption">{{ item.sort_order }}</span>
                </template>

                <!-- Actions -->
                <template #item.actions="{ item }">
                    <div class="d-flex gap-2">
                        <IconBtn v-if="$can('edit', 'screen')" @click="openScreenModal(item)">
                            <VIcon icon="tabler-edit" />
                            <VTooltip activator="parent" location="top">
                                {{ t('admin_notifications.edit') }}
                            </VTooltip>
                        </IconBtn>
                        <IconBtn v-if="$can('edit', 'screen')" :color="item.is_active ? 'warning' : 'success'"
                            @click="toggleScreenActive(item)">
                            <VIcon :icon="item.is_active ? 'tabler-eye-off' : 'tabler-eye'" />
                            <VTooltip activator="parent" location="top">
                                {{ item.is_active ? t('admin_notifications.deactivate') :
                                    t('admin_notifications.activate') }}
                            </VTooltip>
                        </IconBtn>
                        <!-- <IconBtn v-if="$can('delete', 'screen')" color="error" @click="deleteScreen(item)">
                            <VIcon icon="tabler-trash" />
                            <VTooltip activator="parent" location="top">
                                {{ t('admin_notifications.delete') }}
                            </VTooltip>
                        </IconBtn> -->
                    </div>
                </template>

                <template #bottom>
                    <VDivider />

                    <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
                        <p class="text-sm text-disabled mb-0">
                            {{ paginationMeta({ page, itemsPerPage }, totalScreens) }}
                        </p>

                        <VPagination v-model="page" :length="Math.ceil(totalScreens / itemsPerPage)"
                            :total-visible="$vuetify.display.xs ? 1 : Math.ceil(totalScreens / itemsPerPage)">
                            <template #prev="slotProps">
                                <VBtn variant="tonal" color="default" v-bind="slotProps" :icon="false">
                                    {{ $t('admin_notifications.previous') }}
                                </VBtn>
                            </template>

                            <template #next="slotProps">
                                <VBtn variant="tonal" color="default" v-bind="slotProps" :icon="false">
                                    {{ $t('admin_notifications.next') }}
                                </VBtn>
                            </template>
                        </VPagination>
                    </div>
                </template>
            </VDataTableServer>
        </VCard>

        <!-- Screen Modal -->
        <VDialog v-model="isScreenModalVisible" max-width="600px">
            <VCard>
                <VCardTitle>
                    {{ selectedScreen ? t('admin_notifications.edit_screen') : t('admin_notifications.add_new_screen')
                    }}
                </VCardTitle>

                <VCardText>
                    <!-- Error Alert -->
                    <VAlert v-if="Object.keys(errors).length > 0" icon="tabler-alert-circle" type="error"
                        variant="tonal" class="mb-4" closable @click:close="errors = {}">
                        <template #title>
                            {{ errors.general ? t('admin_notifications.error') :
                                t('admin_notifications.validation_errors') }}
                        </template>

                        <ul class="mb-0">
                            <li v-for="(error, field) in errors" :key="field">
                                <span v-if="field === 'general'">{{ error }}</span>
                                <span v-else><strong>{{ field }}:</strong> {{ error }}</span>
                            </li>
                        </ul>
                    </VAlert>

                    <VForm ref="formRef" @submit.prevent="onSubmit">
                        <VRow>
                            <VCol cols="12" md="6">
                                <VTextField v-model="form.value" :label="t('admin_notifications.screen_value')"
                                    :placeholder="t('admin_notifications.enter_screen_value')"
                                    :rules="[v => !!v || t('admin_notifications.value_required')]"
                                    :error-messages="errors.value" type="number" min="1" required />
                            </VCol>

                            <VCol cols="12" md="6">
                                <VTextField v-model="form.sort_order" :label="t('admin_notifications.sort_order')"
                                    :placeholder="t('admin_notifications.enter_sort_order')"
                                    :error-messages="errors.sort_order" type="number" min="0" />
                            </VCol>

                            <VCol cols="12">
                                <VSwitch v-model="form.is_active" :label="t('admin_notifications.is_active')"
                                    :error-messages="errors.is_active" />
                            </VCol>
                        </VRow>
                    </VForm>
                </VCardText>

                <VCardActions class="pa-4 d-flex gap-4 justify-end">
                    <VBtn style="width: 150px;" variant="flat" @click="onSubmit" :loading="isSubmitting">
                        {{ $t('admin_notifications.submit') }}
                    </VBtn>
                    <VBtn color="secondary" variant="tonal" style="width: 150px;" @click="closeScreenModal">
                        {{ $t('admin_notifications.cancel') }}
                    </VBtn>
                </VCardActions>
            </VCard>
        </VDialog>
    </section>
</template>

<script setup>
definePage({
    meta: {
        action: 'view',
        subject: 'screen',
    },
})

import { VDataTableServer } from 'vuetify/labs/VDataTable'
import { paginationMeta } from '@api-utils/paginationMeta'
import $api from "~/utils/api";
import { onMounted, ref, watch, computed } from "vue";
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue'

const { t } = useI18n()

// Set page title
useHead({
    title: t('menu.screens'),
    meta: [
        { name: 'description', content: t('admin_notifications.screens_description') }
    ]
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

// Search
const searchQuery = ref('')
const debouncedSearchQuery = ref('')
let searchTimeout = null

const debounceSearch = (value) => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        debouncedSearchQuery.value = value
    }, 500)
}

const updateOptions = options => {
    page.value = options.page
    sortBy.value = options.sortBy[0]?.key
    orderBy.value = options.sortBy[0]?.order
}

// Headers
const headers = computed(() => [
    {
        title: '#',
        key: 'id',
        sortable: true,
        width: '80px',
    },
    {
        title: t('admin_notifications.value'),
        key: 'value',
        sortable: true,
    },
    {
        title: t('admin_notifications.status'),
        key: 'is_active',
        sortable: true,
    },
    {
        title: t('admin_notifications.sort_order'),
        key: 'sort_order',
        sortable: true,
        width: '120px',
    },
    {
        title: t('admin_notifications.actions'),
        key: 'actions',
        sortable: false,
        width: '150px',
    },
])

const {
    data: screensData,
    execute: fetchScreens,
} = await useApi(createUrl('/api/screens', {
    query: {
        q: debouncedSearchQuery,
        per_page: itemsPerPage,
        page,
        sortBy,
        orderBy,
    },
}))

const screens = computed(() => screensData.value?.data || [])
const totalScreens = computed(() => screensData.value?.meta?.total || 0)

// Modal state
const isScreenModalVisible = ref(false)
const selectedScreen = ref(null)
const isSubmitting = ref(false)
const formRef = ref()
const errors = ref({})

// Form data
const form = ref({
    value: '',
    sort_order: 0,
    is_active: true,
})

// Watch for search query changes to apply debounce
watch(searchQuery, (newValue) => {
    debounceSearch(newValue)
})

// Modal methods
const openScreenModal = (screen = null) => {
    selectedScreen.value = screen
    if (screen) {
        form.value = { ...screen }
    } else {
        form.value = {
            value: '',
            sort_order: 0,
            is_active: true,
        }
    }
    isScreenModalVisible.value = true
}

const closeScreenModal = () => {
    isScreenModalVisible.value = false
    selectedScreen.value = null
    form.value = {
        value: '',
        sort_order: 0,
        is_active: true,
    }
    errors.value = {} // Очищаем ошибки
}

const onSubmit = async () => {
    const { valid } = await formRef.value.validate()
    if (!valid) return

    isSubmitting.value = true
    errors.value = {} // Очищаем предыдущие ошибки

    try {
        if (selectedScreen.value) {
            // Update
            await $api.put(`/screens/${selectedScreen.value.id}`, form.value)
        } else {
            // Create
            await $api.post('/screens', form.value)
        }

        closeScreenModal()
        await fetchScreens()
    } catch (error) {
        console.error('Ошибка при сохранении экрана:', error)

        // Обрабатываем ошибки валидации с сервера
        if (error.response?.data?.errors) {
            errors.value = error.response.data.errors
        } else if (error.response?.data?.message) {
            // Общая ошибка
            errors.value = { general: error.response.data.message }
        } else {
            // Неизвестная ошибка
            errors.value = { general: 'Произошла неизвестная ошибка' }
        }
    } finally {
        isSubmitting.value = false
    }
}

// Toggle screen active status
const toggleScreenActive = async (screen) => {
    try {
        await $api.patch(`/screens/${screen.id}/toggle-active`)
        await fetchScreens()
    } catch (error) {
        console.error('Ошибка при изменении статуса экрана:', error)
    }
}

// Delete screen
const deleteScreen = async (screen) => {
    if (!confirm(t('admin_notifications.confirm_delete_screen', { value: screen.value }))) return

    try {
        await $api.delete(`/screens/${screen.id}`)
        await fetchScreens()
    } catch (error) {
        console.error('Ошибка при удалении экрана:', error)
    }
}

// Load data on mount
onMounted(() => {
    fetchScreens()
})
</script>

<style scoped>
.styled-table :deep(.v-data-table__th) {
    font-weight: bold !important;
    position: relative;
}

.styled-table :deep(.v-data-table__th:not(:last-child))::after {
    content: '';
    position: absolute;
    right: 0;
    top: 30%;
    bottom: 30%;
    width: 2px;
    background-color: rgba(var(--v-theme-on-surface), 0.12);
}

.styled-table :deep(.v-data-table-header__content) {
    font-weight: bold !important;
}
</style>
